
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';

// Define schema for form validation
const formSchema = z.object({
  title: z.string().min(1, { message: 'Title cannot be empty' }),
  slug: z.string().min(1, { message: 'URL slug cannot be empty' }).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, {
    message: 'URL slug can only contain lowercase letters, numbers and hyphens',
  }),
  content_type: z.string(),
  content_body: z.string().optional(),
  excerpt: z.string().optional(),
  status: z.string(),
  publish_now: z.boolean().default(false),
  categories: z.array(z.number()).optional(),
  tags: z.array(z.number()).optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface Category {
  id: number;
  name: string;
}

interface Tag {
  id: number;
  name: string;
}

// Helper function to upload images to the blog-images bucket
const uploadImageToStorage = async (file: File) => {
  const fileExt = file.name.split('.').pop();
  const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
  const filePath = `blog-images/${fileName}`;

  const { data, error } = await supabase.storage
    .from('blog-images')
    .upload(filePath, file);

  if (error) throw error;

  const { data: urlData } = supabase.storage
    .from('blog-images')
    .getPublicUrl(filePath);

  return {
    path: filePath,
    publicUrl: urlData.publicUrl
  };
};

const AdminPostForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState<boolean>(false);
  const [imageUploading, setImageUploading] = useState<boolean>(false);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [selectedTags, setSelectedTags] = useState<number[]>([]);
  const [initialLoading, setInitialLoading] = useState<boolean>(isEditing);

  // Initialize form
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      slug: '',
      content_type: 'blog_post',
      content_body: '',
      excerpt: '',
      status: 'draft',
      publish_now: false,
      categories: [],
      tags: [],
    },
  });

  useEffect(() => {
    // Fetch categories and tags
    const fetchCategoriesAndTags = async () => {
      try {
        const { data: categoriesData, error: categoriesError } = await supabase
          .from('cms_categories')
          .select('*')
          .order('name');

        if (categoriesError) throw categoriesError;
        setCategories(categoriesData || []);

        const { data: tagsData, error: tagsError } = await supabase
          .from('cms_tags')
          .select('*')
          .order('name');

        if (tagsError) throw tagsError;
        setTags(tagsData || []);
      } catch (error) {
        console.error('Error fetching categories and tags:', error);
        toast({
          variant: 'destructive',
          title: 'Failed to get categories and tags',
          description: 'Please try again later',
        });
      }
    };

    fetchCategoriesAndTags();

    // If editing, fetch post data
    if (isEditing && id) {
      const fetchPost = async () => {
        setInitialLoading(true);
        try {
          // Fetch post details
          const { data: post, error: postError } = await supabase
            .from('cms_posts')
            .select('*')
            .eq('id', parseInt(id))
            .single();

          if (postError) throw postError;
          if (!post) throw new Error('Post not found');

          // Set form values
          form.reset({
            title: post.title,
            slug: post.slug,
            content_type: post.content_type,
            content_body: post.content_body || '',
            excerpt: post.excerpt || '',
            status: post.status,
            publish_now: false,
          });

          if (post.featured_image_url) {
            setImageUrl(post.featured_image_url);
          }

          // Fetch related categories
          const { data: postCategories, error: categoriesError } = await supabase
            .from('cms_post_categories')
            .select('category_id')
            .eq('post_id', post.id);

          if (!categoriesError && postCategories) {
            const categoryIds = postCategories.map(item => item.category_id);
            setSelectedCategories(categoryIds);
          }

          // Fetch related tags
          const { data: postTags, error: tagsError } = await supabase
            .from('cms_post_tags')
            .select('tag_id')
            .eq('post_id', post.id);

          if (!tagsError && postTags) {
            const tagIds = postTags.map(item => item.tag_id);
            setSelectedTags(tagIds);
          }
        } catch (error) {
          console.error('Error fetching post:', error);
          toast({
            variant: 'destructive',
            title: 'Failed to get post data',
            description: 'Please try again later',
          });
          navigate('/admin/cms/posts');
        } finally {
          setInitialLoading(false);
        }
      };

      fetchPost();
    }
  }, [isEditing, id, form, navigate, toast]);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImageUploading(true);
    try {
      const { publicUrl } = await uploadImageToStorage(file);
      setImageUrl(publicUrl);
      toast({
        title: 'Upload Successful',
        description: 'Image has been uploaded successfully',
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        variant: 'destructive',
        title: 'Upload Failed',
        description: 'Image upload failed, please try again later',
      });
    } finally {
      setImageUploading(false);
    }
  };

  const onSubmit = async (values: FormValues) => {
    setLoading(true);
    try {
      const now = new Date().toISOString();
      
      // Prepare post data
      const postData = {
        title: values.title,
        slug: values.slug,
        content_type: values.content_type,
        content_body: values.content_body,
        excerpt: values.excerpt,
        status: values.status,
        featured_image_url: imageUrl || null,
        author_id: user?.id || null,
        published_at: values.publish_now ? now : (values.status === 'published' ? now : null),
        updated_at: now,
      };

      let postId: number;

      if (isEditing && id) {
        // Update existing post
        const { data, error } = await supabase
          .from('cms_posts')
          .update(postData)
          .eq('id', parseInt(id))
          .select('id')
          .single();

        if (error) throw error;
        postId = data.id;
        
        // Delete existing categories and tags
        await supabase.from('cms_post_categories').delete().eq('post_id', postId);
        await supabase.from('cms_post_tags').delete().eq('post_id', postId);
      } else {
        // Create new post
        const { data, error } = await supabase
          .from('cms_posts')
          .insert(postData)
          .select('id')
          .single();

        if (error) throw error;
        postId = data.id;
      }

      // Add categories if selected
      if (selectedCategories.length > 0 && postId) {
        const categoryInserts = selectedCategories.map(categoryId => ({
          post_id: postId,
          category_id: categoryId
        }));
        
        const { error: categoryError } = await supabase
          .from('cms_post_categories')
          .insert(categoryInserts);
          
        if (categoryError) throw categoryError;
      }

      // Add tags if selected
      if (selectedTags.length > 0 && postId) {
        const tagInserts = selectedTags.map(tagId => ({
          post_id: postId,
          tag_id: tagId
        }));
        
        const { error: tagError } = await supabase
          .from('cms_post_tags')
          .insert(tagInserts);
          
        if (tagError) throw tagError;
      }

      toast({
        title: isEditing ? 'Update Successful' : 'Creation Successful',
        description: isEditing ? 'Post has been updated successfully' : 'Post has been created successfully',
      });

      navigate('/admin/cms/posts');
    } catch (error) {
      console.error('Error saving post:', error);
      toast({
        variant: 'destructive',
        title: 'Save Failed',
        description: 'An error occurred while saving the post, please try again later',
      });
    } finally {
      setLoading(false);
    }
  };

  // Auto-generate slug from title
  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Watch for title changes to auto-generate slug
  const titleValue = form.watch('title');
  useEffect(() => {
    if (titleValue && !isEditing) {
      const slug = generateSlug(titleValue);
      form.setValue('slug', slug);
    }
  }, [titleValue, form, isEditing]);

  if (initialLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/admin/cms/posts')}
            className="mr-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to List
          </Button>
          <Skeleton className="h-8 w-40" />
        </div>

        <Card>
          <CardHeader>
            <Skeleton className="h-8 w-64" />
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/admin/cms/posts')}
            className="mr-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to List
          </Button>
          <h2 className="text-xl font-semibold">{isEditing ? 'Edit Post' : 'New Post'}</h2>
        </div>
        <Button 
          onClick={form.handleSubmit(onSubmit)} 
          disabled={loading}
          className="bg-amber-600 hover:bg-amber-700"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save
            </>
          )}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Content Information</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter post title" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="slug"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>URL Slug</FormLabel>
                      <FormControl>
                        <Input placeholder="my-post-url" {...field} />
                      </FormControl>
                      <FormDescription>
                        Will be used to generate URL, e.g.: /news/my-post-url
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="content_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Content Type</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select content type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="blog_post">Blog Post</SelectItem>
                          <SelectItem value="news">News</SelectItem>
                          <SelectItem value="notification">Notification</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="published">Published</SelectItem>
                          <SelectItem value="archived">Archived</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="excerpt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Excerpt</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Enter a brief excerpt of the post (optional)" 
                        {...field} 
                        value={field.value || ''}
                      />
                    </FormControl>
                    <FormDescription>
                      Will be displayed on list pages. If not filled, the first few lines of content will be used
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="content_body"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Content</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Enter post body content" 
                        className="min-h-[200px]" 
                        {...field} 
                        value={field.value || ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div>
                <FormLabel className="block mb-2">Featured Image</FormLabel>
                {imageUrl && (
                  <div className="mb-4">
                    <img 
                      src={imageUrl} 
                      alt="Featured" 
                      className="max-h-[200px] rounded-md border object-cover"
                    />
                  </div>
                )}
                <div className="flex items-center">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={imageUploading}
                    className="max-w-md"
                  />
                  {imageUploading && (
                    <div className="ml-2 flex items-center text-sm text-amber-600">
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      Uploading...
                    </div>
                  )}
                </div>
              </div>

              {(categories.length > 0 || tags.length > 0) && (
                <div className="border-t pt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                  {categories.length > 0 && (
                    <div>
                      <FormLabel className="block mb-4">Categories</FormLabel>
                      <div className="space-y-2 max-h-[200px] overflow-y-auto border rounded-md p-4">
                        {categories.map((category) => (
                          <div key={category.id} className="flex items-center space-x-2">
                            <Checkbox 
                              id={`category-${category.id}`} 
                              checked={selectedCategories.includes(category.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedCategories([...selectedCategories, category.id]);
                                } else {
                                  setSelectedCategories(selectedCategories.filter(id => id !== category.id));
                                }
                              }}
                            />
                            <label
                              htmlFor={`category-${category.id}`}
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              {category.name}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {tags.length > 0 && (
                    <div>
                      <FormLabel className="block mb-4">Tags</FormLabel>
                      <div className="space-y-2 max-h-[200px] overflow-y-auto border rounded-md p-4">
                        {tags.map((tag) => (
                          <div key={tag.id} className="flex items-center space-x-2">
                            <Checkbox 
                              id={`tag-${tag.id}`} 
                              checked={selectedTags.includes(tag.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedTags([...selectedTags, tag.id]);
                                } else {
                                  setSelectedTags(selectedTags.filter(id => id !== tag.id));
                                }
                              }}
                            />
                            <label
                              htmlFor={`tag-${tag.id}`}
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              {tag.name}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <FormField
                control={form.control}
                name="publish_now"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>
                        Publish Now
                      </FormLabel>
                      <FormDescription>
                        Check this to use the current time as the publish time
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPostForm;
